// @flow
import React, { PureComponent } from 'react';
import { FormattedMessage, intlShape } from 'react-intl';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { FormValidator } from '../../../../utils/form';
import Modal from '../../../../elements/Modal/Modal';
import Select from '../../../../elements/Select/Select';
import Loading from '../../../../elements/Loading/Loading';
import InputPrice from '../../../../elements/InputPrice/InputPrice';
import { formatToTwoDecimal, shoppingCartUrlVerifier } from '../../../../utils/string';
import { Subject } from 'rxjs';
import update from 'immutability-helper';
import Cleave from 'cleave.js/dist/cleave-react';
import Dropzone from 'react-dropzone';

import style from './ProductModal.scss';
import formStyle from '../../../../assets/styles/form.scss';
import inputStyle from '../../../../assets/styles/input.scss';
import buttonStyle from '../../../../assets/styles/button.scss';
import icons from '../../../../assets/styles/icons.scss';

import type { HTTPError } from '../../../../error';
import type { Category, ScrappedProduct, WishListItem, WishListProduct } from '../../../../entities';
import type { Option } from '../../../../elements/Select/Select';
import type { LocaleState } from '../../../locale/duck';
import type { LocalValuesState } from '../../../local-values/duck';
import type { FormValidationResult } from '../../../../utils/form';


type Props = {
  +localeState: LocaleState;
  +localValues: LocalValuesState;
  +handlePostProduct: (product: WishListProduct) => void;
  +handleEditProduct: (product: WishListProduct, productId: string) => void;
  +handleCloseProductModal: () => void;
  +productModalOpen?: boolean;
  +product?: WishListItem;
  +isPostingProduct?: boolean;
  +isEditingProduct?: boolean;
  +enableWishListTypes: boolean;
  +scrapUrl: (url: string) => void;
  +isScrappingUrl?: boolean;
  +scrappedProduct?: ScrappedProduct;
  +scrapSuccess?: boolean;
  +scrapUrlError?: HTTPError;
};

type State = {
  type: {
    value: FormType;
  };
  categoryId: {
    value: string;
    errors?: string[];
  };
  image: {
    value?: string;
    valueURI?: string;
    pristine: boolean;
    errors?: string[];
  };
  name: {
    value: string;
    errors?: string[];
  };
  description: {
    value: string;
  };
  detailCount: number;
  price: {
    value: string;
    errors?: string[];
  };
  url: {
    value: string;
    errors?: string[];
  };
  showFields?: boolean;
};

type FormType = 'DEAL' | 'PRODUCT';

const initialState: State = {
  type: { value: 'PRODUCT' },
  categoryId: { value: '' },
  image: { pristine: true },
  name: { value: '' },
  description: { value: '' },
  detailCount: 100,
  price: { value: '' },
  url: { value: '' },
  showFields: false,
};

const maxCharactersDetailCount = 100;

export default class ProductModal extends PureComponent<Props, State> {
  productFormValidator: FormValidator;
  dealFormValidator: FormValidator;
  changeUrlSubject$: Subject;
  priceCleave: Cleave;
  state = initialState;

  constructor(props: Props) {
    super(props);
    this.productFormValidator = new FormValidator({
      categoryId: {
        required: 'required',
      },
      image: {
        required: 'required',
      },
      name: {
        required: 'required',
      },
      price: {
        required: 'required',
      },
      url: {
        required: 'required',
        urlFormat: 'urlFormat',
        notCartUrlFormat: shoppingCartUrlVerifier,
      },
    });
    this.dealFormValidator = new FormValidator({
      categoryId: {
        required: 'required',
      },
      image: {
        required: 'required',
      },
      name: {
        required: 'required',
      },
      url: {
        required: 'required',
        urlFormat: 'urlFormat',
        notCartUrlFormat: shoppingCartUrlVerifier,
      },
    });
    this.changeUrlSubject$ = new Subject();
  }

  componentDidUpdate(prevProps: Props) {
    const { productModalOpen, product, scrapSuccess, scrappedProduct, scrapUrlError } = this.props;
    if (!prevProps.productModalOpen && productModalOpen) {
      if (!!product) {
        this.setState({
          categoryId: { value: product.category.id },
          description: { value: product.description },
          image: { value: product.image, pristine: true },
          name: { value: product.name },
          price: { value: product.price || '' },
          url: { value: product.url },
          showFields: true,
          type: { value: !!product.price ? 'PRODUCT' : 'DEAL' },
        });
      } else {
        this.setState({ ...initialState });
        if (!!this.priceCleave) this.priceCleave = undefined;
      }
    }
    if (prevProps.productModalOpen && !productModalOpen) {
      this.setState({ ...initialState });
      if (!!this.priceCleave) this.priceCleave = undefined;
    }
    if (!prevProps.scrapSuccess && scrapSuccess && !!scrappedProduct) {
      this.modifyStateAfterScrap(scrappedProduct);
    }
    if (prevProps.scrapUrlError !== scrapUrlError && !!scrapUrlError) {
      const up: { [key: string]: any } = {};
      const { url } = this.state;
      if (scrapUrlError.data) {
        switch (scrapUrlError.data.name) {
          case 'UrlPathBlacklistError':
            up.url = { value: url.value, errors: ['notCartUrlFormat'] };
            break;
          case 'EbayAuctionError':
            up.url = { value: url.value, errors: ['ebayAuction'] };
            break;
        }
        if (scrapUrlError.code === 400 || scrapUrlError.code === 500) {
          up.showFields = true;
        }
      } else if (scrapUrlError.code === 500) {
        up.url = { value: url.value, errors: ['unscrapped'] };
        up.showFields = true;
      }
      this.setState({ ...up });
    }
  }

  componentDidMount() {
    this.changeUrlSubject$.pipe().subscribe(() => {
      const { url } = this.state;
      const { scrapUrl } = this.props;
      if (!url.errors && url.value) {
        this.setState((prevState: State) => update(prevState, {
          $merge: initialState,
          url: { $set: { value: url.value || '' } },
          showFields: { $set: prevState.showFields },
        }), () => scrapUrl(url.value));
        if (!!this.priceCleave) this.priceCleave.setRawValue('');
      }
    });
  }

  componentWillUnmount() {
    if (this.changeUrlSubject$) {
      this.changeUrlSubject$.unsubscribe();
    }
  }

  handleChangeImage = (acceptedFiles: File[]) => {
    if (acceptedFiles.length) {
      const reader = new FileReader();
      reader.onload = () => {
        const fileAsDataURL = reader.result;
        this.setState({
          image: {
            valueURI: fileAsDataURL.toString(),
            value: '',
            pristine: false,
          },
        });
      };
      reader.onabort = () => console.log('file reading was aborted');
      reader.onerror = () => {
        console.log('error');
        this.setState({
          image: {
            errors: ['maxSize'],
            pristine: false,
          },
        });
        console.log('file reading has failed');
      };
      reader.readAsDataURL(acceptedFiles[0]);
    }
  };

  handleClickDeleteImage = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({
      image: {
        valueURI: '',
        value: '',
        pristine: false,
      },
    });
  };

  handleChangeType = (option: Option) => {
    this.setState({
      type: {
        value: (`${option.value}`: any),
      },
    });
  };

  handleChangeCategory = (option: Option) => {
    this.setState({
      categoryId: {
        value: `${option.value}`,
      },
    });
  };

  handleChangeInputUrl = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      const { value } = e.target;
      this.setState({
        url: { value },
      });
    }
  };

  handleFocusInputUrl = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      const { value } = e.target;
      this.setState({
        url: { value },
      });
    }
  };

  handleChangeInputText = (e: Event) => {
    if (e.target instanceof HTMLInputElement ||
      e.target instanceof HTMLTextAreaElement) {
      const { name, value } = e.target;
      this.setState({
        [name]: {
          value,
        },
      });
    }
  };

  handleChangeTextarea = (e: Event) => {
    if (e.target instanceof HTMLTextAreaElement) {
      const { value } = e.target;
      if (value.length <= maxCharactersDetailCount) {
        this.setState({ detailCount: maxCharactersDetailCount - value.length });
        this.handleChangeInputText(e);
      }
    }
  };

  handleOnKeyPress = (k: SyntheticKeyboardEvent<HTMLInputElement>) => {
    const { charCode } = k;
    if (charCode === 13) {
      this.validateUrl();
    }
  };

  handlePriceInit = (cleave: any) => {
    this.priceCleave = cleave;
  };

  handleChangeCleave = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      // $FlowIgnoreLine: raw value not in target unless Cleave makes a flowtyped (or the community)
      const { rawValue } = e.target;
      this.priceCleave.setRawValue(rawValue);
      this.setState({
        price: {
          value: rawValue,
        },
      });
    }
  };

  handleBlurCleave = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      // $FlowIgnoreLine: raw value not in target unless Cleave makes a flowtyped (or the community)
      const { rawValue } = e.target;
      let newValue = rawValue;
      if (rawValue) {
        newValue = formatToTwoDecimal(rawValue);
      }
      this.priceCleave.setRawValue(newValue);
      this.setState({ price: { value: newValue } });
    }
  };

  handleAddSaveToWishList = () => {
    const isValid = this.validateForm();
    if (!isValid) {
      console.log('handleAddSaveToWishList error!!');
    } else {
      const { handleEditProduct, handlePostProduct, product } = this.props;
      const { categoryId, description, image, name, price, url } = this.state;
      const p: WishListProduct = {
        itemCategoryId: parseInt(categoryId.value, 10),
        name: name.value,
        url: url.value,
      };
      if (!image.pristine) {
        if (image.value) {
          p.image = image.value;
        }
        if (image.valueURI) {
          p.imageURI = image.valueURI;
        }
      }
      if (!!price.value) {
        p.price = price.value;
      }
      if (!!description.value) {
        p.description = description.value;
      }
      if (!!product) {
        // p.id = product.number;
        handleEditProduct(p, `${product.number}`);
      } else {
        handlePostProduct(p);
      }
    }
  };

  modifyStateAfterScrap = (product: ScrappedProduct) => {
    const up: { [key: string]: any } = {
      url: { value: { $set: product.url || '' } },
      name: { value: { $set: product.name || '' } },
      price: { value: { $set: product.price || '' } },
      showFields: { $set: true },
    };
    if (typeof product.categoryId === 'number') {
      up.categoryId = {
        value: { $set: `${product.categoryId}` },
      };
    }
    if (product.image) {
      const imageValue = product.image.startsWith('//') ? `http:${product.image}` : product.image;
      up.image = {
        value: { $set: imageValue },
        pristine: { $set: false },
      };
    }
    this.setState((prevState: State) => update(prevState, up), () => {
      this.priceCleave.setRawValue(product.price || '');
    });
  };

  validateUrl = () => {
    const { url } = this.state;
    const urlErrors = this.productFormValidator.validateField('url', url.value);
    if (urlErrors.length && (urlErrors[0] === 'required' || urlErrors[0] === 'urlFormat'
      || urlErrors[0] === 'notCartUrlFormat')) {
      this.setState({ url: { value: url.value, errors: urlErrors } });
    } else {
      this.changeUrlSubject$.next();
    }
  };

  validateForm = (): boolean => {
    const { categoryId, image, name, price, url, type } = this.state;
    let resp = true;
    let productErrors = this.productFormValidator.validateForm({
      categoryId: categoryId.value,
      image: image.value || image.valueURI,
      name: name.value,
      price: price.value,
      url: url.value,
    });
    if (type.value === 'DEAL') {
      productErrors = this.dealFormValidator.validateForm({
        categoryId: categoryId.value,
        image: image.value || image.valueURI,
        name: name.value,
        url: url.value,
      });
    }
    if (productErrors.length) {
      resp = false;
      productErrors.forEach((error: FormValidationResult) => {
          this.setState((state: State) => update(state, {
            [error.field]: {
              errors: { $set: error.errors },
            },
          }));
        },
      );
    }
    return resp;
  };

  render() {
    const {
      handleCloseProductModal, productModalOpen, localeState, localValues, product,
      isPostingProduct, isEditingProduct, enableWishListTypes, isScrappingUrl,
    } = this.props;
    const { type, categoryId, description, detailCount, image, name, price, url, showFields } = this.state;
    const urlError = url.errors && FormValidator.buildErrorLabel(url.errors, {
      required: 'wishList.search.url.required',
      urlFormat: 'newOrder.product.form.url.error.format',
      notCartUrlFormat: 'newOrder.product.form.url.error.notCartUrlFormat',
      ebayAuction: 'newOrder.product.form.url.error.ebayAuction',
      unscrapped: 'newOrder.product.form.url.error.unscrapped',
    });
    const nameError = name.errors && FormValidator.buildErrorLabel(name.errors, {
      required: 'wishList.search.name.required',
    });
    const priceError = price.errors && FormValidator.buildErrorLabel(price.errors, {
      required: 'wishList.search.price.required',
    });
    const categoryError = categoryId.errors && FormValidator.buildErrorLabel(categoryId.errors, {
      required: 'wishList.search.category.required',
    });
    const imageError = !!image.errors && FormValidator.buildErrorLabel(image.errors, {
      required: 'wishList.search.image.required',
    });
    const categoryOptions: Option[] = (localValues.categories || [])
      .filter((category: Category) => category.enabled)
      .map((category: Category) => {
        return ({
          value: category.id,
          label: category.translations[localeState.locale.toUpperCase()].name,
        })
      });
    const typeOptions = [{
      value: 'PRODUCT',
      label: this.context.intl.formatMessage({ id: 'wishList.search.type.product' }),
    }, {
      value: 'DEAL',
      label: this.context.intl.formatMessage({ id: 'wishList.search.type.deal' }),
    }];
    const dropzone = (
      <article className={style.dropzoneWrap}>
        {(image.value || image.valueURI) &&
        <button type="button"
                className={style.dropzoneDeleteImage}
                onClick={this.handleClickDeleteImage.bind(this)}>
          <i className={icons.iconCloseLight}/>
        </button>}
        <Dropzone accept="image/jpeg,image/jpg,image/png"
                  multiple={false}
                  className={classNames(style.dropzone, {
                    [style.error]: image && image.errors,
                  })}
                  activeClassName={style.active}
                  disabledClassName={style.disabled}
                  maxSize={2097152}
                  onDrop={this.handleChangeImage}>
          {(image && (image.value || image.valueURI)) &&
          <img src={image.value || image.valueURI}
               alt={name.value || ''}/>}

          {(!image || (!image.value && !image.valueURI)) &&
          <section className={style.dropzoneContent}>
            <i className={icons.iconPlusMedium}/>
            <FormattedMessage id="wishList.search.image.label">{
              txt => <p className={style.dropzoneTitle}>{txt}</p>
            }</FormattedMessage>
          </section>}
        </Dropzone>
        {imageError}
      </article>
    );
    return (
      <Modal open={productModalOpen || (isPostingProduct || isEditingProduct)}
             handleClose={handleCloseProductModal}>
        {!showFields && !(isPostingProduct || isEditingProduct) &&
        <section className={style.searchWrap}>
          <FormattedMessage id="wishList.search.title.one">{
            txt => <p className={style.title}>{txt}</p>
          }</FormattedMessage>
          <section className={classNames(style.contentWrap, formStyle.formWrap)}>
            <section className={classNames(formStyle.row, formStyle.inputRow)}>
              <FormattedMessage id="wishList.search.url.label">{
                txt => <label className={formStyle.label}>{txt}</label>
              }</FormattedMessage>
              <FormattedMessage id="newOrder.product.form.url.placeholder">{
                txt => <input name="url"
                              autoComplete="disabled"
                              className={classNames(inputStyle.input, style.urlInput,
                                { [inputStyle.red]: url.errors && url.errors.length },
                                { [inputStyle.lightBlue]: !!url.value })}
                              value={url.value}
                              placeholder={txt}
                              onChange={this.handleChangeInputUrl}
                              onKeyPress={this.handleOnKeyPress}/>
              }</FormattedMessage>
              {isScrappingUrl && <Loading type="sk-three-bounce"
                                          color="lightBlue"
                                          number={3}
                                          className={style.loading}/>}
              {urlError}
            </section>
            <FormattedMessage id="wishList.search.button.one.label">{
              txt => <button onClick={this.validateUrl}
                             disabled={isScrappingUrl}
                             className={classNames(buttonStyle.btn, buttonStyle.lightBlue, style.search)}>{txt}</button>
            }</FormattedMessage>
          </section>
        </section>}
        {!!showFields && !(isPostingProduct || isEditingProduct) &&
        <section className={style.productWrap}>
          <FormattedMessage id="wishList.search.title.two">{
            txt => <p className={style.title}>{txt}</p>
          }</FormattedMessage>
          <section className={classNames(style.contentWrap, formStyle.formWrap)}>
            {enableWishListTypes &&
            <section className={classNames(formStyle.row, formStyle.inputRow)}>
              <FormattedMessage id="wishList.search.type.label">{
                txt => <label className={formStyle.label}>{txt}</label>
              }</FormattedMessage>
              <FormattedMessage id="wishList.search.type.placeholder">{
                txt => <Select id="type"
                               className={classNames({
                                 'light-blue': type.value,
                               })}
                               placeholder={txt}
                               value={type.value}
                               options={typeOptions}
                               handleChange={this.handleChangeType}/>
              }</FormattedMessage>
            </section>}
            <section className={classNames(formStyle.row, formStyle.inputRow)}>
              <FormattedMessage id="wishList.search.url.label">{
                txt => <label className={formStyle.label}>{txt}</label>
              }</FormattedMessage>
              <FormattedMessage id="newOrder.product.form.url.placeholder">{
                txt => <input name="url"
                              autoComplete="disabled"
                              className={classNames(inputStyle.input, style.urlInput,
                                { [inputStyle.red]: url.errors && url.errors.length },
                                { [inputStyle.lightBlue]: !!url.value })}
                              value={url.value}
                              placeholder={txt}
                              onChange={this.handleChangeInputUrl}
                              onFocus={this.handleFocusInputUrl}
                              onKeyPress={this.handleOnKeyPress}/>
              }</FormattedMessage>
              {isScrappingUrl && <Loading type="sk-three-bounce"
                                          color="lightBlue"
                                          number={3}
                                          className={style.loading}/>}
              {urlError}
            </section>
            {dropzone}
            <section className={classNames(formStyle.row, formStyle.inputRow)}>
              <FormattedMessage id="wishList.search.category.label">{
                txt => <label className={formStyle.label}>{txt}</label>
              }</FormattedMessage>
              <FormattedMessage id="wishList.search.category.placeholder">{
                txt => <Select id="categoryId"
                               className={classNames({
                                 'light-blue': categoryId.value,
                                 'red': categoryId.errors,
                               })}
                               hasError={!!categoryId.errors}
                               placeholder={txt}
                               value={categoryId.value}
                               options={categoryOptions}
                               handleChange={this.handleChangeCategory}/>
              }</FormattedMessage>
              {categoryError}
            </section>
            <section className={classNames(formStyle.row, formStyle.inputRow)}>
              <FormattedMessage id="wishList.search.name.label">{
                txt => <label className={formStyle.label}>{txt}</label>
              }</FormattedMessage>
              <FormattedMessage id="wishList.search.name.placeholder">{
                txt => <input name="name"
                              className={classNames(inputStyle.input,
                                { [inputStyle.red]: !!name.errors && name.errors.length },
                                { [inputStyle.lightBlue]: !!name.value })}
                              value={name.value}
                              autoComplete="disabled"
                              placeholder={txt}
                              onChange={this.handleChangeInputText}/>
              }</FormattedMessage>
              {nameError}
            </section>
            <section className={classNames(formStyle.row, formStyle.inputRow)}>
              <FormattedMessage id="wishList.search.price.label">{
                txt => <label className={formStyle.label}>{txt}</label>
              }</FormattedMessage>
              <InputPrice name="price"
                          value={price.value}
                          hasError={!!price.errors}
                          errors={price.errors}
                          handleChangeCleave={this.handleChangeCleave}
                          handleBlurCleave={this.handleBlurCleave}
                          handleInit={this.handlePriceInit}/>
              {priceError}
            </section>
            <section className={classNames(formStyle.row, formStyle.inputRow)}>
              <section className={style.descriptionHeader}>
                <FormattedMessage id="wishList.search.description.label">{
                  txt => (<label className={formStyle.label}>{txt}:</label>)
                }</FormattedMessage>
                <FormattedMessage id="wishList.search.description.count">{
                  txt => (<label className={classNames(formStyle.label, style.counter)}>{detailCount}
                    {detailCount === 1 ? ` ${txt}` : ` ${txt.replace('Ã¡', 'a')}es`}</label>)
                }</FormattedMessage>
              </section>
              <FormattedMessage id="wishList.search.description.placeholder">{
                txt => <textarea name="description"
                                 autoComplete="disabled"
                                 value={description.value}
                                 className={classNames(inputStyle.input, {
                                   [inputStyle.lightBlue]: description && description.value,
                                 })}
                                 onChange={this.handleChangeTextarea}
                                 placeholder={txt}/>
              }</FormattedMessage>
            </section>
            <FormattedMessage id={!product ? 'wishList.search.button.two.label' : 'wishList.save.button.label'}>{
              txt => <button onClick={this.handleAddSaveToWishList}
                             className={classNames(buttonStyle.btn, buttonStyle.lightBlue, style.save)}>{txt}</button>
            }</FormattedMessage>
          </section>
        </section>}
        {(isPostingProduct || isEditingProduct) &&
        <Loading type="circle"
                 color="lightBlue"
                 className={style.loading}/>}
      </Modal>);
  }
}

ProductModal.propTypes = {
  localeState: PropTypes.object.isRequired,
  localValues: PropTypes.object.isRequired,
  handlePostProduct: PropTypes.func.isRequired,
  handleEditProduct: PropTypes.func.isRequired,
  handleCloseProductModal: PropTypes.func.isRequired,
  productModalOpen: PropTypes.bool,
  product: PropTypes.object,
  isPostingProduct: PropTypes.bool,
  isEditingProduct: PropTypes.bool,
  enableWishListTypes: PropTypes.bool.isRequired,
  scrapUrl: PropTypes.func.isRequired,
  isScrappingUrl: PropTypes.bool,
  scrappedProduct: PropTypes.object,
  scrapSuccess: PropTypes.bool,
  scrapUrlError: PropTypes.object,
};

ProductModal.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/wish-list/components/ProductModal/ProductModal.jsx